import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import SearchableSelect from './SearchableSelect';
import { fetchTaskTypeDropdowns } from '../../services/api';
import './TicketTypeSelector.css';

/**
 * System default ticket type configuration (fallback icons, colors, labels)
 * These are only used when API data is not available
 */
export const TICKET_TYPES = {
  epic: {
    icon: '⚡',
    color: '#6554c0',
    bgColor: '#ede7f6',
    label: 'Epic',
    description: 'A large body of work',
    isSubtaskType: false
  },
  story: {
    icon: '★',
    color: '#36b37e',
    bgColor: '#e3fcef',
    label: 'Story',
    description: 'User-facing feature',
    isSubtaskType: false
  },
  task: {
    icon: '✓',
    color: '#4c9aff',
    bgColor: '#deebff',
    label: 'Task',
    description: 'Work item',
    isSubtaskType: false
  },
  subtask: {
    icon: '•',
    color: '#4c9aff',
    bgColor: '#deebff',
    label: 'Subtask',
    description: 'Part of a larger task',
    isSubtaskType: true
  },
  bug: {
    icon: '🐛',
    color: '#ff5630',
    bgColor: '#ffebe6',
    label: 'Bug',
    description: 'Something broken',
    isSubtaskType: false
  }
};

/**
 * Helper to check if a string is an image URL
 */
const isImageUrl = (str) => {
  if (!str) return false;
  return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/api/');
};

/**
 * TicketTypeIcon - Displays ticket type icon with consistent styling
 * Supports both emoji icons and custom icon URLs from MinIO
 */
export const TicketTypeIcon = ({ type, size = 'md', showLabel = false, className = '', config = null }) => {
  // Use provided config or look up from TICKET_TYPES
  const typeConfig = config || TICKET_TYPES[type] || TICKET_TYPES.task;

  const sizeClasses = {
    xs: 'type-icon-xs',
    sm: 'type-icon-sm',
    md: 'type-icon-md',
    lg: 'type-icon-lg'
  };

  // Determine if icon is a URL (custom icon) or emoji (system default)
  const hasImageUrl = isImageUrl(typeConfig.icon);

  return (
    <span
      className={`ticket-type-icon ${sizeClasses[size]} ${className}`}
      style={{
        '--type-color': typeConfig.color,
        '--type-bg': typeConfig.bgColor
      }}
      title={typeConfig.label}
    >
      {hasImageUrl ? (
        <img 
          src={typeConfig.icon} 
          alt={typeConfig.label}
          className="type-icon-image"
          onError={(e) => {
            // Fallback to question mark if image fails to load
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('icon-failed');
            if (e.target.nextElementSibling) {
              e.target.nextElementSibling.style.display = 'inline';
            }
          }}
        />
      ) : (
        <span className="type-icon-symbol">{typeConfig.icon}</span>
      )}
      {hasImageUrl && (
        <span className="type-icon-symbol" style={{ display: 'none' }}>?</span>
      )}
      {showLabel && <span className="type-icon-label">{typeConfig.label}</span>}
    </span>
  );
};

/**
 * TicketTypeBadge - Full badge with background color
 * Supports both emoji icons and custom icon URLs
 */
export const TicketTypeBadge = ({ type, size = 'sm', config = null }) => {
  const typeConfig = config || TICKET_TYPES[type] || TICKET_TYPES.task;
  const hasImageUrl = isImageUrl(typeConfig.icon);

  return (
    <span
      className={`ticket-type-badge ticket-type-badge-${size}`}
      style={{
        backgroundColor: typeConfig.bgColor,
        color: typeConfig.color,
        borderColor: typeConfig.color
      }}
    >
      <span className="badge-icon">
        {hasImageUrl ? (
          <img 
            src={typeConfig.icon} 
            alt={typeConfig.label}
            className="type-icon-image"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextElementSibling) {
                e.target.nextElementSibling.style.display = 'inline';
              }
            }}
          />
        ) : (
          <span>{typeConfig.icon}</span>
        )}
        {hasImageUrl && (
          <span style={{ display: 'none' }}>?</span>
        )}
      </span>
      <span className="badge-label">{typeConfig.label}</span>
    </span>
  );
};

/**
 * TicketTypeSelector - Jira-style dropdown for selecting ticket type
 * 
 * @param {string} value - Currently selected type
 * @param {function} onChange - Callback when type changes
 * @param {array} allowedTypes - Optional array to restrict available types
 * @param {boolean} required - Whether selection is required
 * @param {string} placeholder - Placeholder text when no type selected
 * @param {boolean} compact - Use compact display (icon only until opened)
 * @param {boolean} disabled - Disable the selector
 * @param {string|number} projectId - Optional project ID for scoped type loading
 */
const TicketTypeSelector = ({
  value,
  onChange,
  allowedTypes = null,
  excludeSubtasks = false,
  excludeEpics = false,
  required = false,
  placeholder = 'Select type',
  compact = false,
  disabled = false,
  className = '',
  projectId = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 240 });
  const [customTypes, setCustomTypes] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  // Load custom task types from backend
  useEffect(() => {
    const loadTaskTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await fetchTaskTypeDropdowns(projectId);
        // Convert API response to internal format for merging
        setCustomTypes(types);
      } catch (error) {
        console.error('Error loading custom task types:', error);
        setCustomTypes([]);
      } finally {
        setIsLoadingTypes(false);
      }
    };

    loadTaskTypes();
  }, [projectId]);

  // Merge custom types with system defaults
  const mergedTypes = useMemo(() => {
    // If we have API types, use those exclusively (they include system defaults)
    // Only fall back to TICKET_TYPES if API call fails
    if (customTypes.length > 0) {
      const merged = {};
      customTypes.forEach((customType) => {
        // Use KEY as object key for consistent lookup
        merged[customType.key] = {
          id: customType.id,
          key: customType.key,
          name: customType.name,
          icon: customType.icon_url || TICKET_TYPES[customType.key]?.icon || '📋',
          color: TICKET_TYPES[customType.key]?.color || '#4c9aff',
          bgColor: TICKET_TYPES[customType.key]?.bgColor || '#deebff',
          label: customType.name,
          description: customType.is_system_default ? 'System type' : 'Custom type',
          isCustom: !customType.is_system_default,
          isSubtaskType: customType.is_subtask_type,
          isSystemDefault: customType.is_system_default
        };
      });
      return merged;
    }
    
    // Fall back to static types if no API data
    return { ...TICKET_TYPES };
  }, [customTypes]);

  // Get available types - filter by key and optionally exclude subtasks
  const availableTypes = (() => {
    let entries = Object.entries(mergedTypes);
    if (allowedTypes) {
      entries = entries.filter(([key]) => allowedTypes.includes(key));
    }
    if (excludeSubtasks) {
      entries = entries.filter(([, config]) => !config.isSubtaskType);
    }
    if (excludeEpics) {
      entries = entries.filter(([key]) => key !== 'epic');
    }
    return entries;
  })();

  const selectedConfig = value ? mergedTypes[value] : null;

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = availableTypes.length * 56 + 8; // Estimated height
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    // Prefer opening below - only open above if there's insufficient space below
    // AND significantly more space above (at least 100px more)
    const openAbove = spaceBelow < 200 && spaceAbove > (spaceBelow + 100);

    // Calculate dropdown width and position
    // Use very large safety margin (50px) for side panels where space is constrained
    const dropdownWidth = Math.max(240, triggerRect.width);
    const safetyMargin = 50;
    let leftPosition = triggerRect.left;
    const dropdownRight = leftPosition + dropdownWidth;

    // If dropdown extends beyond right edge of viewport, shift it left
    if (dropdownRight > viewportWidth - safetyMargin) {
      leftPosition = Math.max(safetyMargin, viewportWidth - dropdownWidth - safetyMargin);
    }

    // Ensure dropdown doesn't go off left edge
    if (leftPosition < safetyMargin) {
      leftPosition = safetyMargin;
    }

    setDropdownPosition({
      top: openAbove ? triggerRect.top - dropdownHeight - 4 : triggerRect.bottom + 4,
      left: leftPosition,
      width: dropdownWidth
    });
  }, [availableTypes.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideContainer = containerRef.current?.contains(event.target);
      const isInsideDropdown = listRef.current?.contains(event.target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    
    const handlePositionUpdate = () => calculatePosition();
    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);
    
    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOpen, calculatePosition]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const [typeKey] = availableTypes[highlightedIndex];
          handleSelect(typeKey);
        } else {
          setIsOpen(!isOpen);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < availableTypes.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : availableTypes.length - 1
          );
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        // Type to select - find first match
        const char = e.key.toLowerCase();
        const matchIndex = availableTypes.findIndex(([, config]) =>
          config.label.toLowerCase().startsWith(char)
        );
        if (matchIndex >= 0) {
          setHighlightedIndex(matchIndex);
        }
    }
  };

  const handleSelect = (typeKey) => {
    onChange(typeKey);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        calculatePosition();
      }
      setIsOpen(!isOpen);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`ticket-type-selector ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${compact ? 'is-compact' : ''} ${!value && required ? 'is-required' : ''} ${className}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="type-selector-trigger"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={selectedConfig ? `Type: ${selectedConfig.label}` : placeholder}
      >
        {selectedConfig ? (
          <>
            <span
              className="trigger-icon"
              style={{
                '--type-color': selectedConfig.color,
                '--type-bg': selectedConfig.bgColor
              }}
            >
              {isImageUrl(selectedConfig.icon) ? (
                <img 
                  src={selectedConfig.icon} 
                  alt={selectedConfig.label}
                  className="type-icon-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) {
                      e.target.nextElementSibling.style.display = 'inline';
                    }
                  }}
                />
              ) : (
                <span>{selectedConfig.icon}</span>
              )}
              {isImageUrl(selectedConfig.icon) && (
                <span style={{ display: 'none' }}>?</span>
              )}
            </span>
            {!compact && <span className="trigger-label">{selectedConfig.label}</span>}
          </>
        ) : (
          <>
            <span className="trigger-placeholder-icon">?</span>
            {!compact && <span className="trigger-placeholder">{placeholder}</span>}
          </>
        )}
        <span className="trigger-arrow">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 8L2 4h8z" />
          </svg>
        </span>
      </button>

      {isOpen && createPortal(
        <div
          ref={listRef}
          className="type-selector-dropdown"
          role="listbox"
          aria-label="Select ticket type"
          data-ignore-modal-close
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            minWidth: `${dropdownPosition.width}px`,
            zIndex: 10001 // Above modal overlays
          }}
        >
          {availableTypes.map(([typeKey, config], index) => (
            <button
              key={typeKey}
              type="button"
              className={`type-option ${value === typeKey ? 'is-selected' : ''} ${highlightedIndex === index ? 'is-highlighted' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(typeKey);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              role="option"
              aria-selected={value === typeKey}
            >
              <span
                className="option-icon"
                style={{
                  '--type-color': config.color,
                  '--type-bg': config.bgColor
                }}
              >
                {isImageUrl(config.icon) ? (
                  <img 
                    src={config.icon} 
                    alt={config.label}
                    className="type-icon-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextElementSibling) {
                        e.target.nextElementSibling.style.display = 'inline';
                      }
                    }}
                  />
                ) : (
                  <span>{config.icon}</span>
                )}
                {isImageUrl(config.icon) && (
                  <span style={{ display: 'none' }}>?</span>
                )}
              </span>
              <span className="option-content">
                <span className="option-label">{config.label}</span>
                <span className="option-description">{config.description}</span>
              </span>
              {value === typeKey && (
                <span className="option-check">✓</span>
              )}
            </button>
          ))}
        </div>,
        document.body // Render dropdown to body to prevent clipping
      )}
    </div>
  );
};

/**
 * InlineTypeSelector - Compact type selector for use within input fields
 */
export const InlineTypeSelector = ({
  value,
  onChange,
  allowedTypes = null,
  excludeSubtasks = false,
  excludeEpics = false,
  disabled = false,
  projectId = null
}) => {
  // Inline selector's dropdown should also prevent modal overlay from closing when clicked
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openAbove: false });
  const [customTypes, setCustomTypes] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  // Load custom task types from backend
  useEffect(() => {
    const loadTaskTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await fetchTaskTypeDropdowns(projectId);
        setCustomTypes(types);
      } catch (error) {
        console.error('Error loading custom task types:', error);
        setCustomTypes([]);
      } finally {
        setIsLoadingTypes(false);
      }
    };

    loadTaskTypes();
  }, [projectId]);

  // Merge custom types with system defaults
  const mergedTypes = useMemo(() => {
    // If we have API types, use those exclusively (they include system defaults)
    // Only fall back to TICKET_TYPES if API call fails
    if (customTypes.length > 0) {
      const merged = {};
      customTypes.forEach((customType) => {
        // Use KEY as object key for consistent lookup
        merged[customType.key] = {
          id: customType.id,
          key: customType.key,
          name: customType.name,
          icon: customType.icon_url || TICKET_TYPES[customType.key]?.icon || '📋',
          color: TICKET_TYPES[customType.key]?.color || '#4c9aff',
          bgColor: TICKET_TYPES[customType.key]?.bgColor || '#deebff',
          label: customType.name,
          description: customType.is_system_default ? 'System type' : 'Custom type',
          isCustom: !customType.is_system_default,
          isSubtaskType: customType.is_subtask_type,
          isSystemDefault: customType.is_system_default
        };
      });
      return merged;
    }
    
    // Fall back to static types if no API data
    return { ...TICKET_TYPES };
  }, [customTypes]);

  const availableTypes = (() => {
    let entries = Object.entries(mergedTypes);
    if (allowedTypes) {
      entries = entries.filter(([key]) => allowedTypes.includes(key));
    }
    if (excludeSubtasks) {
      entries = entries.filter(([, config]) => !config.isSubtaskType);
    }
    if (excludeEpics) {
      entries = entries.filter(([key]) => key !== 'epic');
    }
    return entries;
  })();

  const selectedConfig = value ? mergedTypes[value] : null;
  const dropdownRef = useRef(null);

  // Calculate dropdown position when opening
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = availableTypes.length * 40 + 8; // Estimated height
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    // Prefer opening below - only open above if there's insufficient space below
    // AND significantly more space above (at least 100px more)
    const openAbove = spaceBelow < 200 && spaceAbove > (spaceBelow + 100);

    // Calculate left position with viewport edge detection
    // Use very large safety margin (50px) for side panels where space is constrained
    const dropdownWidth = 200; // Approximate width for inline dropdown
    const safetyMargin = 50;
    let leftPosition = triggerRect.left;
    const dropdownRight = leftPosition + dropdownWidth;

    // If dropdown extends beyond right edge of viewport, shift it left
    if (dropdownRight > viewportWidth - safetyMargin) {
      leftPosition = Math.max(safetyMargin, viewportWidth - dropdownWidth - safetyMargin);
    }

    // Ensure dropdown doesn't go off left edge
    if (leftPosition < safetyMargin) {
      leftPosition = safetyMargin;
    }

    setDropdownPosition({
      top: openAbove ? triggerRect.top - dropdownHeight - 4 : triggerRect.bottom + 4,
      left: leftPosition,
      openAbove
    });
  }, [availableTypes.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideContainer = containerRef.current?.contains(event.target);
      const isInsideDropdown = dropdownRef.current?.contains(event.target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    
    const handlePositionUpdate = () => calculatePosition();
    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);
    
    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOpen, calculatePosition]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (typeKey) => {
    onChange(typeKey);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`inline-type-selector ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="inline-type-trigger"
        onClick={handleToggle}
        disabled={disabled}
        title={selectedConfig ? `${selectedConfig.label} - Click to change` : 'Select type'}
      >
        {selectedConfig ? (
          <span
            className="inline-type-icon"
            style={{
              '--type-color': selectedConfig.color,
              '--type-bg': selectedConfig.bgColor
            }}
          >
            {isImageUrl(selectedConfig.icon) ? (
              <img 
                src={selectedConfig.icon} 
                alt={selectedConfig.label}
                className="type-icon-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextElementSibling) {
                    e.target.nextElementSibling.style.display = 'inline';
                  }
                }}
              />
            ) : (
              <span>{selectedConfig.icon}</span>
            )}
            {isImageUrl(selectedConfig.icon) && (
              <span style={{ display: 'none' }}>?</span>
            )}
          </span>
        ) : (
          <span className="inline-type-placeholder">?</span>
        )}
        <span className="inline-type-caret">▾</span>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="inline-type-dropdown"
          data-ignore-modal-close
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            zIndex: 10001 // Above modal overlays
          }}
        >
          {availableTypes.map(([typeKey, config]) => (
            <button
              key={typeKey}
              type="button"
              className={`inline-type-option ${value === typeKey ? 'is-selected' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(typeKey);
              }}
              title={config.label}
            >
              <span
                className="inline-option-icon"
                style={{
                  '--type-color': config.color,
                  '--type-bg': config.bgColor
                }}
              >
                {isImageUrl(config.icon) ? (
                  <img 
                    src={config.icon} 
                    alt={config.label}
                    className="type-icon-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextElementSibling) {
                        e.target.nextElementSibling.style.display = 'inline';
                      }
                    }}
                  />
                ) : (
                  <span>{config.icon}</span>
                )}
                {isImageUrl(config.icon) && (
                  <span style={{ display: 'none' }}>?</span>
                )}
              </span>
              <span className="inline-option-label">{config.label}</span>
            </button>
          ))}
        </div>,
        document.body // Render dropdown to body to prevent clipping
      )}
    </div>
  );
};

export default TicketTypeSelector;
